import { DownloadOutlined, UploadOutlined } from '@mui/icons-material'
import { Stack } from '@mui/material'
import axios from 'axios'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import { useAlertContext } from '@/contexts/AlertContext'
import { ConfigurationStatusIndicator } from '@/features/controller/components/bulkActions/ConfigurationStatusIndicator'
import { useControllerConfigurationFileSetContext } from '@/features/controller/configuration/contexts/ControllerConfigurationFileSetContext'
import type { ControllerConfigurationSourceUpdate } from '@/features/controller/configuration/models/controllerConfiguration'
import { useRestartControllerMutation } from '@/features/controller/restart/hooks/useRestartControllerMutation'
import { ControllerRestartMethod } from '@/features/controller/restart/types'
import type { ControllerDescription } from '@/features/controller/types'

export interface ConfigurationColumnProps {
  configuration: ControllerConfigurationSourceUpdate
  configurationUpdateInProgress: boolean
  controller: ControllerDescription
  isSelected: boolean
  onLoadConfigurationFromController: (update: ControllerConfigurationSourceUpdate) => void
}

export function ConfigurationColumn({
  configuration,
  configurationUpdateInProgress,
  controller,
  isSelected,
  onLoadConfigurationFromController,
}: Readonly<ConfigurationColumnProps>) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const { appJson, raspiProperties } = useControllerConfigurationFileSetContext()
  const {
    restartController,
    isSuccess: isRestartRequested,
    isPending: isRestartPending,
  } = useRestartControllerMutation(controller.id, ControllerRestartMethod.SSH)

  if (appJson.isLatestVersionLoading || raspiProperties.isLatestVersionLoading) {
    return (
      <ConfigurationStatusIndicator
        inProgress={true}
        message={t('controllers.bulk_actions.configuration_column.loading')}
      />
    )
  }

  const appJsonMatches = configuration?.appJsonText === appJson.latestVersion?.value
  const raspiPropertiesMatches = configuration?.raspiPropertiesText === raspiProperties.latestVersion?.value
  const allConfigurationMatches = appJsonMatches && raspiPropertiesMatches

  const isUpdatePending = appJson.isUpdatePending || raspiProperties.isUpdatePending

  function handleCopyConfigurationFromThisController() {
    onLoadConfigurationFromController({
      appJsonText: appJson.latestVersion?.value,
      raspiPropertiesText: raspiProperties.latestVersion?.value,
      comment: `Configuration copied from controller ${controller.equipmentNumber}`,
    })
  }

  async function handleSaveConfigurationToThisController(restart: boolean) {
    if (allConfigurationMatches || !configurationUpdateInProgress) {
      // No need to do anything, target is not set or already has target configuration
      return
    }
    try {
      if (configuration?.raspiPropertiesText !== undefined && !raspiPropertiesMatches) {
        await raspiProperties.updateAndInvalidate({
          previousVersionId: raspiProperties.latestVersion!.id,
          value: configuration.raspiPropertiesText,
          comment: configuration.comment,
        })
      }
      if (configuration?.appJsonText !== undefined && !appJsonMatches) {
        await appJson.updateAndInvalidate({
          previousVersionId: appJson.latestVersion!.id,
          value: configuration.appJsonText,
          comment: configuration.comment,
        })
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        pushAlert({
          message: t('controllers.bulk_actions.configuration_column.save_error_conflict'),
          severity: 'error',
        })
      } else {
        pushAlert({
          message: t('controllers.bulk_actions.configuration_column.save_error'),
          severity: 'error',
        })
      }
      // Don't restart if the update failed
      return
    }
    if (restart) {
      try {
        await restartController()
      } catch {
        pushAlert({
          message: t('controllers.configuration.restart_error'),
          severity: 'error',
        })
      }
    }
  }

  function renderSaveButtons() {
    return (
      <>
        <CustomButton
          color="primary"
          disabled={isUpdatePending}
          startIcon={<UploadOutlined />}
          variant="contained"
          onClick={() => handleSaveConfigurationToThisController(false)}
        >
          {t('controllers.bulk_actions.configuration_column.save')}
        </CustomButton>
        <CustomButton
          color="primary"
          disabled={isUpdatePending}
          startIcon={<UploadOutlined />}
          variant="contained"
          onClick={() => handleSaveConfigurationToThisController(true)}
        >
          {t('controllers.bulk_actions.configuration_column.save_and_restart')}
        </CustomButton>
      </>
    )
  }

  function getStatusIndicatorProps() {
    // Handle restart states when configuration matches
    if (allConfigurationMatches && isRestartPending) {
      return {
        inProgress: true,
        message: t('controllers.bulk_actions.configuration_column.restart_pending'),
      }
    }

    if (allConfigurationMatches && isRestartRequested) {
      return {
        message: t('controllers.bulk_actions.configuration_column.restart_requested'),
        success: true,
      }
    }

    // Handle configuration match
    if (allConfigurationMatches) {
      return {
        message: t('controllers.bulk_actions.configuration_column.matches'),
        success: true,
      }
    }

    // Handle update in progress
    if (isUpdatePending) {
      return {
        inProgress: true,
        message: t('controllers.bulk_actions.configuration_column.updating'),
      }
    }

    // Default pending state
    return {
      message: t('controllers.bulk_actions.configuration_column.pending'),
    }
  }

  function renderStatus() {
    const statusProps = getStatusIndicatorProps()

    return (
      <>
        <ConfigurationStatusIndicator {...statusProps} />
        {!allConfigurationMatches && configurationUpdateInProgress && renderSaveButtons()}
      </>
    )
  }

  return (
    <Stack alignItems="center" direction="row" gap={1}>
      <CustomButton
        color="secondary"
        startIcon={<DownloadOutlined />}
        variant="outlined"
        onClick={handleCopyConfigurationFromThisController}
      >
        {t('controllers.bulk_actions.configuration_column.load')}
      </CustomButton>
      {isSelected ? (
        <>
          {t('controllers.bulk_actions.configuration_column.configuration')}: {renderStatus()}
        </>
      ) : (
        <Stack>&nbsp;</Stack>
      )}
    </Stack>
  )
}
